// Ctrl+K: la herramienta principal para moverse por el ERP. Ejecuta COMANDOS («nueva nota de venta»,
// «abrir stock») y busca a la vez, según permisos y contratos que existan, clientes, proveedores,
// productos, notas de venta / pedidos, documentos tributarios (factura, boleta, guía, NC, ND) y
// órdenes de compra; los resultados salen agrupados por tipo. Un prefijo acota el número: «factura
// 123», «boleta 5», «guía 7», «nc 3», «nv 120», «oc 45». Vacío, sugiere las acciones y los favoritos.
// Flechas para moverse, Enter abre, Esc cierra. Lo que todavía no tiene búsqueda en el backend se dice
// al pie (no se inventa).
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { api } from '../api/cliente';
import type { ClienteEncontrado } from '../api/contratos';
import { useDebounce } from '../api/consultas';
import { Modal } from '../comun/Modal';
import { fecha, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { buscarComandos, normalizar, PANTALLAS, type Pantalla } from './navegacion';
import { useFavoritos } from './preferencias';
import { useModoRevision } from './salud';

/** Prefijo que acota la búsqueda por número a un tipo de documento. */
const PREFIJOS: Record<
  string,
  { grupo: 'notas' | 'dte' | 'oc' | 'od' | 'pago' | 'cot'; tipoDte?: number }
> = {
  od: { grupo: 'od' },
  pago: { grupo: 'pago' },
  cot: { grupo: 'cot' },
  cotizacion: { grupo: 'cot' },
  nv: { grupo: 'notas' },
  v: { grupo: 'notas' },
  nota: { grupo: 'notas' },
  pedido: { grupo: 'notas' },
  factura: { grupo: 'dte', tipoDte: 33 },
  boleta: { grupo: 'dte', tipoDte: 39 },
  guia: { grupo: 'dte', tipoDte: 52 },
  nc: { grupo: 'dte', tipoDte: 61 },
  nd: { grupo: 'dte', tipoDte: 56 },
  oc: { grupo: 'oc' },
};

/** «factura 123» → { numero: '123', prefijo: factura }; «NV-000120» → numero 120; «bot» → null. */
export function leerNumero(t: string) {
  const m = /^([a-zA-ZáéíóúÁÉÍÓÚ]*)[\s.-]*(?:V-)?0*(\d{1,10})$/.exec(t.trim());
  if (!m) return null;
  const pre = normalizar(m[1]!);
  if ((pre && !PREFIJOS[pre]) || m[2] === '0') return null;
  return { numero: m[2]!, prefijo: pre ? PREFIJOS[pre]! : null };
}

export type ResultadoGlobal =
  | { tipo: 'pantalla'; pantalla: Pantalla }
  | { tipo: 'cliente'; id: string; razonSocial: string }
  | { tipo: 'proveedor'; id: string; razonSocial: string }
  | { tipo: 'producto'; id: string; texto: string; nombre: string }
  | { tipo: 'nota'; id: string; numeroVisible: string }
  | { tipo: 'dte'; id: string; tipoDte: number; folio: number | null; origenId: string | null }
  | { tipo: 'oc'; id: string; numero: string }
  | { tipo: 'od'; id: string; numero: number }
  | { tipo: 'pago'; id: string; numero: number }
  | { tipo: 'cotizacion'; id: string; numeroVisible: string }
  | { tipo: 'ruta'; id: string; nombre: string };

interface Fila {
  clave: string;
  principal: string;
  secundario?: string;
  resultado: ResultadoGlobal;
}

interface Grupo {
  id: string;
  nombre: string;
  filas: Fila[];
  cargando: boolean;
  error: boolean;
}

/** Nombre del tipo SII (lo que el usuario busca: factura, boleta, guía, NC, ND). */
export const NOMBRE_TIPO_DTE: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  52: 'Guía de despacho',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};

export function BuscadorGlobal({
  alElegir,
  alCerrar,
}: {
  alElegir: (r: ResultadoGlobal) => void;
  alCerrar: () => void;
}) {
  const { empresa, puede, usuario } = useSesion();
  const empresaId = empresa!.id;
  const modoRevision = useModoRevision();
  const { favoritos } = useFavoritos(usuario?.id);
  const [texto, setTexto] = useState('');
  const [sel, setSel] = useState(0);
  const t = useDebounce(texto.trim());
  const leido = leerNumero(t);
  const numero = leido?.numero ?? '';
  const solo = leido?.prefijo?.grupo;
  const tipoDte = leido?.prefijo?.tipoDte;
  // Con número no se buscan nombres (un «12» no es un cliente); con texto, sí.
  const conTexto = t.length >= 2 && !leido;

  const consultas = [
    {
      id: 'clientes',
      nombre: 'Clientes',
      activa: conTexto && puede('entidades.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<ClienteEncontrado[]>('/entidades', {
            query: { empresaId, rol: 'cliente', texto: t, limite: '6' },
          })
        ).map((c) => ({
          clave: `c:${c.id}`,
          principal: c.razon_social,
          secundario: rut(c.rut, c.dv),
          resultado: { tipo: 'cliente', id: c.id, razonSocial: c.razon_social },
        })),
    },
    {
      id: 'productos',
      nombre: 'Productos',
      activa: conTexto && puede('catalogo.productos.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<Array<{ id: string; sku: string; nombre: string; estado: string }>>(
            '/productos',
            { query: { empresaId, texto: t, limite: '6' } },
          )
        ).map((p) => ({
          clave: `p:${p.id}`,
          principal: p.nombre,
          secundario: `${p.sku}${p.estado !== 'activo' ? ` · ${p.estado}` : ''}`,
          resultado: { tipo: 'producto', id: p.id, texto: p.sku, nombre: p.nombre },
        })),
    },
    {
      id: 'notas',
      nombre: 'Notas de venta · pedidos',
      activa: !!numero && (!solo || solo === 'notas') && puede('ventas.nota.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<{
            items: Array<{
              id: string;
              numeroVisible: string;
              cliente_razon_social: string;
              estado: string;
            }>;
          }>('/ventas/notas', { query: { empresaId, numero, tamano: 5 } })
        ).items.map((n) => ({
          clave: `n:${n.id}`,
          principal: n.numeroVisible,
          secundario: `${n.cliente_razon_social} · ${n.estado}`,
          resultado: { tipo: 'nota', id: n.id, numeroVisible: n.numeroVisible },
        })),
    },
    {
      id: 'dte',
      nombre: 'Facturas · boletas · guías · NC · ND',
      activa: !!numero && (!solo || solo === 'dte') && puede('dte.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<
            Array<{
              id: string;
              tipo_dte: number;
              folio: number | null;
              estado: string;
              origen_id: string | null;
              receptor_razon_social?: string | null;
              monto_total?: string | null;
              fecha_emision?: string | null;
            }>
          >('/dte/documentos', { query: { empresaId, folio: numero, tipoDte } })
        ).map((d) => ({
          clave: `d:${d.id}`,
          principal: `${NOMBRE_TIPO_DTE[d.tipo_dte] ?? `DTE ${d.tipo_dte}`} N° ${d.folio ?? '—'}`,
          // El documento se reconoce por su cliente, monto y fecha (Enter abre su ficha).
          secundario:
            [
              d.receptor_razon_social,
              d.monto_total ? pesos(d.monto_total) : null,
              d.fecha_emision ? fecha(d.fecha_emision) : null,
            ]
              .filter(Boolean)
              .join(' · ') || d.estado,
          resultado: {
            tipo: 'dte',
            id: d.id,
            tipoDte: d.tipo_dte,
            folio: d.folio,
            origenId: d.origen_id,
          },
        })),
    },
    {
      id: 'oc',
      nombre: 'Órdenes de compra',
      activa: !!numero && (!solo || solo === 'oc') && puede('compras.oc.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<{
            items: Array<{
              id: string;
              numero: string;
              proveedor_razon_social?: string | null;
              estado: string;
            }>;
          }>('/compras/ordenes', { query: { empresaId, numero, tamano: 5 } })
        ).items.map((o) => ({
          clave: `o:${o.id}`,
          principal: `OC ${o.numero}`,
          secundario: `${o.proveedor_razon_social ?? ''} · ${o.estado}`,
          resultado: { tipo: 'oc', id: o.id, numero: String(o.numero) },
        })),
    },
    {
      id: 'od',
      nombre: 'Órdenes de despacho',
      activa: !!numero && (!solo || solo === 'od') && puede('logistica.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<
            Array<{ id: string; numero: number; estado: string; fechaNegocio?: string }>
          >('/logistica/ordenes', { query: { empresaId, numero } })
        ).map((o) => ({
          clave: `od:${o.id}`,
          principal: `ODD ${o.numero}`,
          secundario: o.estado.toLowerCase().replace(/_/g, ' '),
          resultado: { tipo: 'od', id: o.id, numero: o.numero },
        })),
    },
    {
      id: 'pagos',
      nombre: 'Pagos a proveedores',
      activa: !!numero && (!solo || solo === 'pago') && puede('tesoreria.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<Array<{ pagoId: string; numero: number; estado: string; total: string }>>(
            '/tesoreria/pagos',
            { query: { empresaId } },
          )
        )
          .filter((p) => String(p.numero) === numero)
          .map((p) => ({
            clave: `pg:${p.pagoId}`,
            principal: `Pago N° ${p.numero}`,
            secundario: `${p.estado.toLowerCase().replace(/_/g, ' ')} · $${p.total}`,
            resultado: { tipo: 'pago', id: p.pagoId, numero: p.numero },
          })),
    },
    {
      id: 'cotizaciones',
      nombre: 'Cotizaciones',
      activa: !!numero && (!solo || solo === 'cot') && puede('ventas.cotizacion.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<{
            items: Array<{
              id: string;
              numero: string;
              numeroVisible: string;
              cliente_razon_social: string;
              estado: string;
            }>;
          }>('/ventas/cotizaciones', { query: { empresaId, tamano: 200 } })
        ).items
          .filter((c) => String(Number(c.numero)) === numero)
          .map((c) => ({
            clave: `cot:${c.id}`,
            principal: c.numeroVisible,
            secundario: `${c.cliente_razon_social} · ${c.estado.toLowerCase()}`,
            resultado: { tipo: 'cotizacion', id: c.id, numeroVisible: c.numeroVisible },
          })),
    },
    {
      id: 'rutas',
      nombre: 'Rutas de reparto',
      activa: conTexto && puede('logistica.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<Array<{ id: string; codigo: string; nombre: string }>>('/logistica/rutas', {
            query: { empresaId },
          })
        )
          .filter((x) => `${x.codigo} ${x.nombre}`.toLowerCase().includes(t.toLowerCase()))
          .slice(0, 5)
          .map((x) => ({
            clave: `ru:${x.id}`,
            principal: `${x.codigo} · ${x.nombre}`,
            secundario: 'Ruta de reparto',
            resultado: { tipo: 'ruta', id: x.id, nombre: x.nombre },
          })),
    },
    {
      id: 'proveedores',
      nombre: 'Proveedores',
      activa: conTexto && puede('entidades.ver'),
      fn: async (): Promise<Fila[]> =>
        (
          await api.get<ClienteEncontrado[]>('/entidades', {
            query: { empresaId, rol: 'proveedor', texto: t, limite: '4' },
          })
        ).map((c) => ({
          clave: `v:${c.id}`,
          principal: c.razon_social,
          secundario: rut(c.rut, c.dv),
          resultado: { tipo: 'proveedor', id: c.id, razonSocial: c.razon_social },
        })),
    },
  ];
  const resultados = useQueries({
    queries: consultas.map((c) => ({
      queryKey: ['emp', empresaId, 'buscar-global', c.id, t, numero, tipoDte ?? null],
      queryFn: c.fn,
      enabled: c.activa,
      staleTime: 30_000,
      retry: false,
    })),
  });

  const visible = useCallback(
    (p: Pantalla): boolean =>
      (!p.permiso || puede(p.permiso)) && (p.ruta !== 'configuracion/mapa' || modoRevision),
    [puede, modoRevision],
  );
  const filaComando = (p: (typeof PANTALLAS)[number]): Fila => ({
    clave: `r:${p.ruta}`,
    principal: p.nombre,
    secundario: p.accion
      ? `Acción · ${p.modulo.nombre}${p.tecla ? ` · ${p.tecla}` : ''}`
      : `Abrir · ${p.modulo.nombre}${p.seccion ? ` › ${p.seccion}` : ''}`,
    resultado: { tipo: 'pantalla', pantalla: p },
  });
  const comandos: Fila[] = useMemo(() => {
    if (t.length >= 2 && !leido) return buscarComandos(t, visible).map(filaComando);
    if (t) return [];
    // Vacío: las acciones y los favoritos, para no tener que escribir nada.
    const acciones = PANTALLAS.filter((p) => p.accion && visible(p));
    const favs = favoritos
      .map((r) => PANTALLAS.find((p) => p.ruta === r) ?? null)
      .filter((p): p is (typeof PANTALLAS)[number] => !!p && !p.accion && visible(p));
    return [...acciones, ...favs].map(filaComando);
  }, [t, visible, favoritos, leido]);

  // Primero los comandos (lo que se quiere HACER), después lo que se busca (clientes, documentos…).
  const grupos: Grupo[] = [
    {
      id: 'comandos',
      nombre: t ? 'Comandos' : 'Sugeridos',
      filas: comandos,
      cargando: false,
      error: false,
    },
    ...consultas.map((c, i) => ({
      id: c.id,
      nombre: c.nombre,
      filas: c.activa ? (resultados[i]!.data ?? []) : [],
      cargando: c.activa && resultados[i]!.isFetching,
      error: c.activa && resultados[i]!.isError,
    })),
  ].filter((g) => g.filas.length || g.cargando || g.error);
  const planas = grupos.flatMap((g) => g.filas);
  const actual = Math.min(sel, Math.max(0, planas.length - 1));

  return (
    <Modal titulo="Buscar en el ERP (Ctrl+K)" alCerrar={alCerrar} ancho="ancho">
      <input
        data-autofoco
        className="buscador"
        aria-label="Buscar en el ERP"
        placeholder="Comando, cliente, producto, N° de nota o pedido, «factura 123», «oc 45»…"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setSel(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setSel(Math.min(planas.length - 1, actual + 1));
          else if (e.key === 'ArrowUp') setSel(Math.max(0, actual - 1));
          else if (e.key === 'Enter' && planas[actual]) alElegir(planas[actual].resultado);
          else return;
          e.preventDefault();
        }}
      />
      <div className="resultados-globales" role="listbox" aria-label="Resultados">
        {t.length === 1 && !numero && (
          <p className="apagado">
            Escribe al menos 2 letras, o un número para buscar notas, folios y OC.
          </p>
        )}
        {t.length >= 2 && !grupos.length && <p className="apagado">Sin resultados para «{t}».</p>}
        {grupos.map((g) => (
          <div key={g.id} className="grupo-resultados" role="group" aria-label={g.nombre}>
            <div className="titulo-grupo">
              {g.nombre}
              {g.cargando && <span className="apagado"> · buscando…</span>}
              {g.error && <span className="tono-malo"> · no se pudo buscar</span>}
            </div>
            {g.filas.map((f) => {
              const i = planas.indexOf(f);
              return (
                <button
                  key={f.clave}
                  type="button"
                  role="option"
                  aria-selected={i === actual}
                  className={`resultado${i === actual ? ' sel' : ''}`}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => alElegir(f.resultado)}
                >
                  <span className="principal">{f.principal}</span>
                  {f.secundario && <span className="secundario">{f.secundario}</span>}
                </button>
              );
            })}
          </div>
        ))}
        <p className="apagado pie-buscador">
          Todavía sin búsqueda en el backend: ODD (orden de despacho), cotizaciones por número,
          recepciones y documentos de compra.
        </p>
      </div>
    </Modal>
  );
}
